import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Citation } from "@/api/q-and-a";
import type { GraphFullReference } from "@/api/graph-metadata";
import { graphCitationIsGrounded } from "@/components/doc-viewer/utils/graphCitations";

// Re-exported so existing type references keep working; the canonical
// definition lives with the shared GraphMetadata contract in the api layer.
export type { GraphFullReference };

/** Concise value condensed from a graph_full narrative. Rendered only when
 * the backend stamped `graph_metadata.value_is_extracted` — without the flag
 * the answer text is a truncated lede that stays hidden behind the dialog.
 * Shared by every surface that shows the View Answer trigger so the value
 * can't ship on one surface and be missed on another. */
export const GraphFullExtractedValue: React.FC<{
  value?: string | null;
  isExtracted?: boolean;
  className?: string;
}> = ({ value, isExtracted, className }) =>
  isExtracted && value?.trim() ? (
    <p className={`text-sm font-medium text-gray-900 break-words ${className ?? ""}`}>
      {value}
    </p>
  ) : null;

// How a `[N]` chip should navigate. `groundedByDoc` maps a document_id to the
// grounded citation id for it (a citation whose bbox was matched to the
// answer's supporting line); `groupId` is the answer group. With both, a chip
// deep-links to the exact highlight; without, it falls back to a page-level
// reference.
export interface CitationLinkContext {
  projectId: string;
  groupId?: string;
  // marker ([N]) -> grounded citation id. Preferred, so a document cited by
  // multiple markers deep-links each chip to ITS OWN citation.
  groundedByMarker: Map<number, string>;
  // document_id -> first grounded citation id. ONLY used when there's no marker
  // data at all (doc-viewer / Q&A surfaces, and older answers without the map).
  groundedByDoc: Map<string, string>;
  // True when this answer carries per-marker grounding. When true, a marker
  // that's absent from `groundedByMarker` means that marker didn't ground (page
  // level) — NOT that we're missing data, so we must NOT fall back to the
  // document's first grounded citation.
  hasMarkerData: boolean;
}

/** Walks rendered text children and replaces `[N]` substrings with a
 * <CitationPill> when N is in the references map. Unresolvable markers
 * stay as plain text. */
const renderTextWithCitations = (
  children: React.ReactNode,
  refsByMarker: Map<number, GraphFullReference>,
  ctx?: CitationLinkContext,
): React.ReactNode => {
  if (refsByMarker.size === 0 || !ctx) return children;
  const visit = (node: React.ReactNode, key: string): React.ReactNode => {
    if (typeof node === "string") {
      return splitCitations(node, refsByMarker, ctx, key);
    }
    if (Array.isArray(node)) {
      return node.map((child, i) => visit(child, `${key}-${i}`));
    }
    return node;
  };
  return visit(children, "root");
};

const _CITATION_RE = /\[(\d+)\]/g;
const splitCitations = (
  text: string,
  refsByMarker: Map<number, GraphFullReference>,
  ctx: CitationLinkContext,
  keyPrefix: string,
): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  _CITATION_RE.lastIndex = 0;
  while ((match = _CITATION_RE.exec(text)) !== null) {
    const marker = Number(match[1]);
    const ref = refsByMarker.get(marker);
    if (!ref) continue;
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <CitationPill
        key={`${keyPrefix}-c${i++}`}
        marker={marker}
        reference={ref}
        ctx={ctx}
      />,
    );
    last = match.index + match[0].length;
  }
  if (parts.length === 0) return text;
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

// A graph reference navigates by the GROUNDED citation when we have one for its
// document (group + citation id -> the viewer selects the question and scrolls
// to the exact highlight). Otherwise it has no exact span, so it navigates by
// PAGE (`docPage`) — the honest page-level landing.
//
// (The projects view reads `?page=` as its list-pagination cursor, so we use
// `docPage`, never `page`, for the page-level fallback.)
export const buildGraphCitationHref = (
  reference: GraphFullReference,
  ctx: CitationLinkContext,
): string => {
  // With per-marker data, each [N] resolves to ITS OWN citation — and a marker
  // that didn't ground gets no citation (page-level below), never the doc's
  // first grounded one. Only without marker data (older answers / doc-viewer /
  // Q&A) do we fall back to the document's first grounded citation.
  const groundedCitationId = ctx.hasMarkerData
    ? ctx.groundedByMarker.get(reference.marker)
    : ctx.groundedByDoc.get(reference.document_id);
  const params = new URLSearchParams({
    docId: reference.document_id,
    open: "true",
  });
  if (groundedCitationId && ctx.groupId) {
    params.set("groupId", ctx.groupId);
    params.set("citationId", groundedCitationId);
  } else if (reference.page != null) {
    params.set("docPage", String(reference.page));
  }
  return `/projects/${ctx.projectId}/documents?${params.toString()}`;
};

export interface GroundedMaps {
  groundedByMarker: Map<number, string>;
  groundedByDoc: Map<string, string>;
  hasMarkerData: boolean;
}

/** Resolve grounded-citation lookup maps for a graph answer's chips. Prefers a
 * pre-resolved `groundedCitations` list (report surface, whose citation shape is
 * thinner but carries the [N] marker); otherwise derives the document map from
 * full `citations` via graphCitationIsGrounded (doc-viewer / Q&A surfaces, which
 * have no per-marker data and so key by document only). `hasMarkerData` is true
 * once any grounded citation carries a marker — the caller then treats an absent
 * marker as "didn't ground" rather than "unknown". */
export const resolveGroundedMaps = (
  citations?: Citation[],
  groundedCitations?: { id: string; document_id: string; marker?: number | null }[],
): GroundedMaps => {
  const groundedByMarker = new Map<number, string>();
  const groundedByDoc = new Map<string, string>();
  let hasMarkerData = false;
  if (groundedCitations) {
    for (const g of groundedCitations) {
      if (!g.document_id) continue;
      if (!groundedByDoc.has(g.document_id)) groundedByDoc.set(g.document_id, g.id);
      if (g.marker != null) {
        hasMarkerData = true;
        if (!groundedByMarker.has(g.marker)) groundedByMarker.set(g.marker, g.id);
      }
    }
    return { groundedByMarker, groundedByDoc, hasMarkerData };
  }
  for (const c of citations ?? []) {
    const docId = "document_id" in c ? c.document_id : null;
    if (docId && graphCitationIsGrounded(c) && !groundedByDoc.has(docId)) {
      groundedByDoc.set(docId, c.id);
    }
  }
  return { groundedByMarker, groundedByDoc, hasMarkerData: false };
};

const CitationPill: React.FC<{
  marker: number;
  reference: GraphFullReference;
  ctx: CitationLinkContext;
}> = ({ marker, reference, ctx }) => {
  const href = buildGraphCitationHref(reference, ctx);
  const tooltip = [reference.filename, reference.page != null ? `p. ${reference.page}` : null]
    .filter(Boolean)
    .join(" — ");
  // Use window.open rather than <Link> / <a> because Radix Dialog renders
  // via a portal outside the React Router provider tree — Link/anchor
  // navigation behaves inconsistently there. The citations modal uses the
  // same pattern (see question-row.tsx:showDocument).
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(href, "_blank");
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      title={tooltip || undefined}
      className="mx-0.5 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-blue-700 align-baseline hover:bg-blue-100 hover:text-blue-900"
    >
      [{marker}]
    </button>
  );
};

/**
 * Renders the long-form `graph_full` markdown answer in a dialog. Triggered
 * from `ExtractedValueCell` when an answer carries
 * `graph_metadata.full_markdown`.
 *
 * Component map mirrors the one used in `chats/chat-message.tsx` so styling
 * stays consistent across markdown surfaces in the app.
 */
export const GraphFullAnswerDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variableName?: string;
  questionText?: string;
  markdown: string;
  references?: GraphFullReference[];
  projectId?: string;
  // The answer's citations + its group id let each `[N]` chip deep-link to the
  // grounded citation for its document (scroll to the highlight, select the
  // question) instead of a bare page. `groundedCitations` is a pre-resolved
  // alternative (used by the report surface, whose citation shape is thinner) —
  // when provided it's used directly instead of deriving from `citations`.
  citations?: Citation[];
  groundedCitations?: { id: string; document_id: string; marker?: number | null }[];
  groupId?: string;
}> = ({ open, onOpenChange, variableName, questionText, markdown, references, projectId, citations, groundedCitations, groupId }) => {
  const heading = variableName || questionText || "Graph Full Answer";
  const refsByMarker = React.useMemo(() => {
    const m = new Map<number, GraphFullReference>();
    for (const r of references ?? []) m.set(r.marker, r);
    return m;
  }, [references]);
  const linkCtx = React.useMemo<CitationLinkContext | undefined>(() => {
    if (!projectId) return undefined;
    return {
      projectId,
      groupId,
      ...resolveGroundedMaps(citations, groundedCitations),
    };
  }, [projectId, groupId, citations, groundedCitations]);
  const transform = React.useCallback(
    (children: React.ReactNode) =>
      renderTextWithCitations(children, refsByMarker, linkCtx),
    [refsByMarker, linkCtx],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="text-lg font-semibold">{heading}</DialogTitle>
          {questionText && variableName ? (
            <p className="mt-1 text-sm text-muted-foreground">{questionText}</p>
          ) : null}
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4 text-sm text-gray-900">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => (
                <p className="mb-2 last:mb-0">{transform(children)}</p>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold">{transform(children)}</strong>
              ),
              ul: ({ children }) => (
                <ul className="mb-2 ml-4 list-disc space-y-1 last:mb-0">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="mb-2 ml-4 list-decimal space-y-1 last:mb-0">{children}</ol>
              ),
              li: ({ children }) => <li className="pl-1">{transform(children)}</li>,
              h1: ({ children }) => (
                <h3 className="mb-1 mt-3 text-base font-bold first:mt-0">{transform(children)}</h3>
              ),
              h2: ({ children }) => (
                <h4 className="mb-1 mt-3 text-sm font-bold first:mt-0">{transform(children)}</h4>
              ),
              h3: ({ children }) => (
                <h5 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{transform(children)}</h5>
              ),
              code: ({ children, className }) => {
                const isBlock = className?.includes("language-");
                if (isBlock)
                  return (
                    <code className="mb-2 block rounded bg-black/5 p-2 text-xs last:mb-0">
                      {children}
                    </code>
                  );
                return (
                  <code className="rounded bg-black/5 px-1 py-0.5 text-xs">
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => <pre className="mb-2 last:mb-0">{children}</pre>,
              blockquote: ({ children }) => (
                <blockquote className="mb-2 border-l-2 border-current/20 pl-3 italic last:mb-0">
                  {children}
                </blockquote>
              ),
              hr: () => <hr className="my-2 border-current/10" />,
              a: ({ href, children, ...props }) => {
                const linkClasses = "font-semibold text-blue-600 hover:underline";
                if (href)
                  return (
                    <Link to={href} className={linkClasses} {...props}>
                      {children}
                    </Link>
                  );
                return <span>{children}</span>;
              },
            }}
          >
            {markdown}
          </ReactMarkdown>
        </div>
      </DialogContent>
    </Dialog>
  );
};
